/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { EntityStore } from 'src/core/types/EntityStore';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import {
    CustomTextExportedProjectDocumentSwagger,
    CustomTextExportedProjectEntityLabelSwagger,
    CustomTextExportedProjectEntityRegionSwagger,
    CustomTextExportedProjectEntityRegionSwagger as CustomTextExportedProjectExtractorRegionSwagger,
    CustomTextExportedProjectModelSwagger,
    CustomTextExportedProjectSwagger,
    CustomTextProjectSwagger
} from 'src/customText/core/services/projects/customTextProjectSwaggers';
import { CustomTextClassificationStore } from 'src/customText/core/stores/CustomTextClassificationStore';
import { CustomTextClassStore } from 'src/customText/core/stores/CustomTextClassStore';
import {
    CustomTextDocumentMetadata,
    CustomTextDocumentStore,
    emptyCustomTextDocumentMetadata
} from 'src/customText/core/stores/CustomTextDocumentStore';
import { CustomTextEntityLabelStore } from 'src/customText/core/stores/CustomTextEntityLabelStore';
import { CustomTextExtractionStore } from 'src/customText/core/stores/CustomTextExtractionStore';
import { CustomTextProjectKind, CustomTextProjectStore } from 'src/customText/core/stores/CustomTextProjectStore';

const entityLabelSwaggersToEntityLabelStores = (
    entityLabelSwaggers: CustomTextExportedProjectEntityLabelSwagger[],
    entityStores: EntityStore[]
): CustomTextEntityLabelStore[] =>
    entityLabelSwaggers.map(
        entityLabel =>
            new CustomTextEntityLabelStore({
                entityId: entityStores.find(es => es.name === entityLabel.category).id,
                startCharIndex: entityLabel.offset,
                // Example: 7ammo .. start: 0, length: 5 .. therefore end: 0 + 5 - 1 = 4
                endCharIndex: entityLabel.offset + entityLabel.length - 1,
                labelType: entityLabel.autoTagged ? 'autoTagged' : 'normal'
            })
    );

export const entityLabelingRegionSwaggerToEntityLabelStores = (
    documentEntity: CustomTextExportedProjectExtractorRegionSwagger,
    entityStores: EntityStore[]
): CustomTextEntityLabelStore[] => entityLabelSwaggersToEntityLabelStores(documentEntity.labels, entityStores);

export const entityNamesToEntityStores = (entityNames: string[]): EntityStore[] =>
    entityNames.map(entityName => new EntityStore({ name: entityName }));

export const trainingDocumentSwaggerToDocumentStore = (
    documentSwagger: CustomTextExportedProjectDocumentSwagger,
    entityStores: EntityStore[]
): CustomTextDocumentStore =>
    new CustomTextDocumentStore({
        name: documentSwagger.location,
        language: documentSwagger.language,
        entityLabels: documentEntityLabelsToEntityLabelStores(documentSwagger.entities, entityStores)
    });

export const documentEntityLabelsToEntityLabelStores = (
    documentEntityLabels: CustomTextExportedProjectExtractorRegionSwagger[],
    entityStores: EntityStore[]
): CustomTextEntityLabelStore[] =>
    documentEntityLabels?.reduce(
        (accumulator, current) => accumulator.concat(entityLabelingRegionSwaggerToEntityLabelStores(current, entityStores)),
        []
    ) ?? [];

export const documentClassLabelsToClassLabelStores = (
    documentClassLabels: CustomTextExportedProjectModelSwagger[],
    classesStores: CustomTextClassStore[]
): CustomTextClassStore[] => documentClassLabels?.map(label => classesStores.find(cs => cs.name === label.category)) ?? [];

export const projectSwaggerToProjectStore = (
    projectSwagger: CustomTextProjectSwagger,
    documents?: CustomTextDocumentMetadata[],
    modelStore?: CustomTextExtractionStore | CustomTextClassificationStore
) =>
    new CustomTextProjectStore(
        {
            documents,
            name: projectSwagger.projectName,
            culture: projectSwagger.language,
            oosThreshold: projectSwagger.settings?.confidenceThreshold,
            projectKind: projectSwagger.projectKind,
            description: projectSwagger.description,
            containerName: projectSwagger.storageInputContainerName,
            amlProjectPath: projectSwagger.settings?.amlProjectPath,
            isLabelingLocked: projectSwagger.settings?.isLabelingLocked,
            amlWorkspaceContainerName: projectSwagger.settings?.amlContainerName,
            multiLingual: projectSwagger.multiLingual || projectSwagger.multilingual,
            createdDate: projectSwagger.createdDateTime && new Date(projectSwagger.createdDateTime),
            modifiedDate: projectSwagger.lastModifiedDateTime && new Date(projectSwagger.lastModifiedDateTime)
        },
        modelStore
    );

export const exportedProjectSwaggerToDocumentAndTagStores = (
    exportedProjectSwagger: CustomTextExportedProjectSwagger,
    documentsMetadata: CustomTextDocumentMetadata[],
    projectKind: CustomTextProjectKind
): {
    documents: CustomTextDocumentMetadata[];
    tagStores: EntityStore[] | CustomTextClassStore[];
} => {
    const { documents: exportedDocuments = [], classes = [], entities = [] } = exportedProjectSwagger.assets;

    const isSingleClassification = projectKind === 'CustomSingleLabelClassification';

    const unLabeledDocs = documentsMetadata.filter(d => !exportedDocuments.some(ed => d.name === ed.location));
    const allDocsWithoutLabels: CustomTextDocumentMetadata[] = exportedDocuments
        .map(document => ({
            ...emptyCustomTextDocumentMetadata,
            name: document.location,
            documentDataset: document.dataset || 'Train',
            language: document.language || exportedProjectSwagger.metadata.language,
            contentSize: documentsMetadata.find(doc => doc.name === document.location)?.contentSize ?? 0
        }))
        .concat(unLabeledDocs);

    let tagStores: EntityStore[] | CustomTextClassStore[] = [];

    const isExtraction = exportedProjectSwagger.metadata.projectKind === 'CustomEntityRecognition';
    tagStores = isExtraction
        ? entityNamesToEntityStores(entities.map(e => e.category))
        : classes.map(cl => new CustomTextClassStore({ name: cl.category }));

    const allDocsWithLabels = allDocsWithoutLabels.map(doc => {
        const documentInLabels = exportedDocuments.find(d => d.location === doc.name);
        if (documentInLabels) {
            if (isExtraction) {
                return {
                    ...doc,
                    language: documentInLabels.language,
                    entityLabels: documentEntityLabelsToEntityLabelStores(documentInLabels.entities, <EntityStore[]>tagStores)
                };
            }

            if (isSingleClassification) {
                return {
                    ...doc,
                    language: documentInLabels.language,
                    classLabels: documentClassLabelsToClassLabelStores([documentInLabels.class], <CustomTextClassStore[]>tagStores)
                };
            }

            return {
                ...doc,
                language: documentInLabels.language,
                classLabels: documentClassLabelsToClassLabelStores(documentInLabels.classes, <CustomTextClassStore[]>tagStores)
            };
        }

        return doc;
    });

    return { documents: allDocsWithLabels, tagStores };
};

export const projectStoreToExportedProjectSwagger = (projectStore: CustomTextProjectStore): CustomTextExportedProjectSwagger => {
    const isExtraction = projectStore.projectKind === 'CustomEntityRecognition';
    const isSingleClassification = projectStore.projectKind === 'CustomSingleLabelClassification';
    const isMultiClassification = projectStore.projectKind === 'CustomMultiLabelClassification';

    let documents: CustomTextExportedProjectDocumentSwagger[];

    if (isExtraction) {
        documents = projectStore.documents
            .filter(d => d.entityLabels.length !== 0)
            .map<CustomTextExportedProjectDocumentSwagger>(d => {
                const minIndex = d.entityLabels
                    .map(l => l.startCharIndex)
                    .reduce((prev, curr) => (prev < curr ? prev : curr), d.entityLabels[0]?.startCharIndex ?? 0);
                const maxIndex = d.entityLabels
                    .map(l => l.endCharIndex)
                    .reduce((prev, curr) => (prev > curr ? prev : curr), d.entityLabels[0]?.endCharIndex ?? 0);

                return <CustomTextExportedProjectDocumentSwagger>{
                    location: d.name,
                    language: d.language,
                    dataset: d.documentDataset,
                    entities: [
                        <CustomTextExportedProjectEntityRegionSwagger>{
                            regionOffset: FeatureToggles.CTLabelingRegion ? null : minIndex,
                            regionLength: FeatureToggles.CTLabelingRegion ? null : maxIndex - minIndex + 1,
                            labels: d.entityLabels.map(
                                el =>
                                    <CustomTextExportedProjectEntityLabelSwagger>{
                                        category: projectStore.extractionStore.entities.find(e => e.id === el.entityId).name,
                                        offset: el.startCharIndex,
                                        autoTagged: el.labelType === 'autoTagged',
                                        length: el.endCharIndex - el.startCharIndex + 1
                                    }
                            )
                        }
                    ].filter(e => e.labels.length)
                };
            })
            .filter(d => d.entities.length);
    } else {
        documents = projectStore.documents
            .filter(d => d.classLabels.length !== 0)
            .map<CustomTextExportedProjectDocumentSwagger>(d => ({
                location: d.name,
                language: d.language,
                dataset: d.documentDataset,
                class: isSingleClassification && d.classLabels.length ? { category: d.classLabels[0].name } : undefined,
                classes: isMultiClassification
                    ? d.classLabels.map<CustomTextExportedProjectModelSwagger>(cl => ({ category: cl.name }))
                    : undefined
            }));
    }

    return {
        metadata: {
            projectName: projectStore.name,
            language: projectStore.culture,
            projectKind: projectStore.projectKind,
            multiLingual: projectStore.multiLingual,
            multilingual: projectStore.multiLingual,
            description: projectStore.description,
            storageInputContainerName: projectStore.containerName,
            settings: { confidenceThreshold: projectStore.oosThreshold }
        },
        assets: {
            documents,
            projectKind: projectStore.projectKind,
            classes: isExtraction
                ? undefined
                : projectStore.classificationStore.classes.map<CustomTextExportedProjectModelSwagger>(cl => ({ category: cl.name })),
            entities: isExtraction
                ? projectStore.extractionStore.entities.map<CustomTextExportedProjectModelSwagger>(el => ({ category: el.name }))
                : undefined
        },
        stringIndexType: 'Utf16CodeUnit'
    };
};

export const documentStoreToDocumentMetadata = (store: CustomTextDocumentStore): CustomTextDocumentMetadata => ({
    name: store.name,
    text: store.text,
    contentSize: store.contentSize,
    language: store.language,
    isInitialized: store.isInitialized,
    entityLabels: [...store.entityLabels],
    documentDataset: store.documentDataset,
    classLabels: store.classLabels.map(cl => new CustomTextClassStore({ ...cl }))
});

export const documentMetadataToDocumentStore = (metadata: CustomTextDocumentMetadata) =>
    new CustomTextDocumentStore({ ...metadata, classLabels: metadata.classLabels.map(cl => new CustomTextClassStore(cl)) });

export const getCustomTextProjectImportDataFromFileContent = (fileContent: string) => {
    const projectSwagger: CustomTextExportedProjectSwagger = JSON.parse(fileContent);

    return {
        projectKind: projectSwagger.metadata?.projectKind,
        projectName: projectSwagger.metadata?.projectName
    };
};
